import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './Toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
}

export interface ToastProps extends Toast {
  /** Callback when toast is dismissed */
  onDismiss: (id: string) => void;
  /** Auto-dismiss duration in ms (0 to disable) */
  duration?: number;
}

export const ToastItem: React.FC<ToastProps> = ({
  id,
  type,
  title,
  description,
  onDismiss,
  duration = 5000,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onDismiss(id), 200);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onDismiss]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(id), 200);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M16.6668 5L7.50016 14.1667L3.3335 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 6.66667V10M10 13.3333H10.0083M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 6.66667V10M10 13.3333H10.0083M8.57465 3.21533L1.51632 15.0003C1.37082 15.252 1.29379 15.5377 1.29298 15.8288C1.29216 16.1199 1.36759 16.406 1.51167 16.6585C1.65575 16.911 1.86359 17.1211 2.11461 17.2679C2.36563 17.4146 2.65073 17.4929 2.94132 17.4953H17.058C17.3486 17.4929 17.6337 17.4146 17.8847 17.2679C18.1357 17.1211 18.3436 16.911 18.4876 16.6585C18.6317 16.406 18.7071 16.1199 18.7063 15.8288C18.7055 15.5377 18.6285 15.252 18.483 15.0003L11.4247 3.21533C11.2764 2.97067 11.0673 2.76846 10.8175 2.62816C10.5677 2.48787 10.286 2.41431 9.99965 2.41431C9.71327 2.41431 9.43158 2.48787 9.18177 2.62816C8.93196 2.76846 8.72289 2.97067 8.57465 3.21533Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'info':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 13.3333V10M10 6.66667H10.0083M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
    }
  };

  return (
    <div className={`ui-toast ui-toast--${type} ${isExiting ? 'ui-toast--exiting' : ''}`}>
      <div className="ui-toast__icon">{getIcon()}</div>
      <div className="ui-toast__content">
        <div className="ui-toast__title">{title}</div>
        {description && <div className="ui-toast__description">{description}</div>}
      </div>
      <button
        type="button"
        className="ui-toast__close"
        onClick={handleDismiss}
        aria-label="Dismiss notification"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M12 4L4 12M4 4L12 12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

export interface ToastContainerProps {
  /** Array of toasts to display */
  toasts: Toast[];
  /** Callback when toast is dismissed */
  onDismiss: (id: string) => void;
  /** Position of toast container */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onDismiss,
  position = 'top-right',
}) => {
  if (toasts.length === 0) return null;

  return createPortal(
    <div className={`ui-toast-container ui-toast-container--${position}`}>
      {toasts.map((toast) => (
        <ToastItem key={toast.id} {...toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body
  );
};

ToastContainer.displayName = 'ToastContainer';
